import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DateTime } from "luxon";

import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../../components/shared/Inputs/TextInput";
import ClickToCopy from "../../../components/shared/ClickToCopy";
import ConfirmModal from "../../../components/shared/Confirm/ConfirmModal";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../../../components/shared/StatusPill";
import useSendingDomains from "../../../data/useSendingDomains";
import useNotification from "../../../components/notifications/useNotifications";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import { ISendingDomain } from "../../../models/sendingDomain/ISendingDomain";
import formatDateTime from "../../../utils/formatDateTime";

type AddDomainForm = {
  domain: string;
};

export default function SendingDomainSettings() {
  const { setInfo } = usePageNameContext();
  const { domains, add, verify, remove } = useSendingDomains();
  const { addNotification } = useNotification();
  const [pendingRemove, setPendingRemove] = useState<ISendingDomain | null>(null);
  const { register, handleSubmit, reset, watch } = useForm<AddDomainForm>({
    defaultValues: { domain: "" },
  });
  const domainValue = watch("domain");

  useEffect(() => {
    setInfo({
      name: "Sending domain",
      desc: "Verify your own domain so outbound mail can be sent from your addresses",
    });
  }, [setInfo]);

  const handleAdd = (data: AddDomainForm) => {
    const value = data.domain.trim();
    if (!value) return;

    add.mutate(value, {
      onSuccess: () => reset(),
    });
  };

  const handleVerify = (id: string) => {
    verify.mutate(id, {
      onSuccess: (res) => {
        addNotification({
          variant: res.data?.verified ? "success" : "warn",
          primaryText: res.message,
        });
      },
      onError: (err: unknown) => {
        addNotification({
          variant: "error",
          primaryText: err instanceof Error ? err.message : "Failed to verify sending domain",
        });
      },
    });
  };

  const rows = domains.data?.data ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Add the domain you send mail from. Copy the CNAME records into your DNS, then verify.
        Sending is ready once DKIM and return-path are verified. Link tracking is optional.
      </p>

      <form
        onSubmit={handleSubmit(handleAdd)}
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
      >
        <TextInput
          label="Domain"
          register={register}
          registerName="domain"
          placeholder="example.com"
          cols="sm:max-w-sm flex-1"
        />
        <ButtonInput
          label="Add domain"
          isSubmit={true}
          classes=""
          onClick={undefined}
          disabled={!domainValue?.trim()}
          isLoading={add.isLoading}
        />
      </form>

      {domains.isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingWheel />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No sending domains yet.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => (
            <div key={row.id} className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-gray-900">{row.domain}</p>
                  <StatusPill
                    text={row.verified ? "Verified" : "Pending"}
                    colour={row.verified ? StatusPillColour.Green : StatusPillColour.Yellow}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <ButtonInput
                    label="Verify"
                    classes=""
                    onClick={() => handleVerify(row.id)}
                    isLoading={verify.isLoading && verify.variables === row.id}
                    disabled={verify.isLoading}
                  />
                  <ButtonInput
                    label="Remove"
                    classes="!bg-white !text-red-700 !border-red-200 hover:!bg-red-50"
                    onClick={() => setPendingRemove(row)}
                  />
                </div>
              </div>
              {row.verifiedAt ? (
                <p className="text-xs text-gray-500">
                  Verified {formatDateTime(row.verifiedAt, DateTime.DATETIME_SHORT)}
                </p>
              ) : null}
              {row.dnsRecords?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-medium uppercase text-gray-500">
                        <th className="py-2 pr-4">Purpose</th>
                        <th className="py-2 pr-4">Type</th>
                        <th className="py-2 pr-4">Host</th>
                        <th className="py-2 pr-4">Value</th>
                        <th className="py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {row.dnsRecords.map((record) => (
                        <tr key={`${row.id}-${record.purpose}-${record.host}`}>
                          <td className="py-2 pr-4 text-gray-700">{record.purpose}</td>
                          <td className="py-2 pr-4 text-gray-700">{record.type}</td>
                          <td className="py-2 pr-4 font-mono text-xs">
                            <ClickToCopy text={record.host} textClassName="font-mono text-xs" />
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs">
                            <ClickToCopy text={record.value} textClassName="font-mono text-xs" />
                          </td>
                          <td className="py-2">
                            <StatusPill
                              text={record.verified ? "Verified" : record.status || "Pending"}
                              colour={record.verified ? StatusPillColour.Green : StatusPillColour.Yellow}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!pendingRemove}
        setOpen={(open: boolean) => {
          if (!open) setPendingRemove(null);
        }}
        title="Remove sending domain"
        message={
          pendingRemove
            ? `Remove ${pendingRemove.domain}? Mail from this domain will no longer be authenticated until you add it again.`
            : ""
        }
        confirmButtonText="Remove"
        isLoading={remove.isLoading}
        onConfirm={() => {
          if (!pendingRemove) return;
          remove.mutate(pendingRemove.id, {
            onSuccess: () => setPendingRemove(null),
          });
        }}
      />
    </div>
  );
}
